import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Chip,
    CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface RestoreFromGlacierDialogProps {
    open: boolean;
    onClose: () => void;
    selectedFilesCount: number;
    onRestore: (days: number) => Promise<void>;
}

const RestoreFromGlacierDialog: React.FC<RestoreFromGlacierDialogProps> = ({
    open,
    onClose,
    selectedFilesCount,
    onRestore,
}) => {
    const [days, setDays] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [restoreLoader, setRestoreLoader] = useState<boolean>(false);

    const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        // Allow empty string for user to clear the field
        if (value === "") {
            setDays("");
            setError("");
            return;
        }

        // Check if it's a valid positive integer
        const numValue = parseInt(value, 10);

        if (isNaN(numValue) || !Number.isInteger(parseFloat(value))) {
            setError("Please enter a valid integer");
            return;
        }

        if (numValue < 1) {
            setError("Minimum value is 1 day");
            setDays(value);
            return;
        }

        setError("");
        setDays(value);
    };

    const handleRestore = async () => {
        setRestoreLoader(true);
        const numDays = parseInt(days, 10);

        if (!days || isNaN(numDays) || numDays < 1) {
            setError("Please enter a valid number of days (minimum 1)");
            return;
        }

        await onRestore(numDays);
        handleClose();
    };

    const handleClose = () => {
        setDays("");
        setError("");
        onClose();
        setRestoreLoader(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="h6" component="span">
                        Restore from Glacier
                    </Typography>
                    <Chip
                        label={`${selectedFilesCount} file${
                            selectedFilesCount !== 1 ? "s" : ""
                        } selected`}
                        color="primary"
                        size="small"
                    />
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 3,
                            p: 1.5,
                            bgcolor: "background.paper",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: 1,
                            gap: 1,
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{ color: "text.secondary" }}
                            fontSize="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                            The restored copy is moved back to Glacier
                            automatically after a specified number of days.
                        </Typography>
                    </Box>

                    <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ mb: 1, fontWeight: 600 }}
                    >
                        Number of days that the restored copy is available
                    </Typography>

                    <TextField
                        fullWidth
                        type="number"
                        value={days}
                        onChange={handleDaysChange}
                        error={!!error}
                        helperText={error}
                        placeholder="Enter number of days"
                        inputProps={{
                            min: 1,
                            step: 1,
                        }}
                        sx={{ mb: 2 }}
                    />

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                    >
                        Bulk retrieval - Typically within 48 hours.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleRestore}
                    variant="contained"
                    color="primary"
                    disabled={!days || !!error || restoreLoader}
                >
                    Restore{" "}
                    {restoreLoader && (
                        <CircularProgress size={20} color="inherit" />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RestoreFromGlacierDialog;
